import { useCallback, useEffect, useState } from "react";
import { Archive, ClockCounterClockwise, Lock, MagnifyingGlass, Plus, Trash, WarningCircle } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WealthShell } from "@/pages/viveka/wealth/WealthShell";
import { AssetForm } from "@/pages/viveka/wealth/AssetForm";
import { LiabilityPanel } from "@/pages/viveka/wealth/LiabilityPanel";
import { BUCKET_TONE, cr, inr, useWealthConfig } from "@/lib/wealth";
import { toast } from "sonner";

export default function AssetRegister() {
  const { tenantId } = useAuth();
  const config = useWealthConfig();
  const [assets, setAssets] = useState([]);
  const [entities, setEntities] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [tab, setTab] = useState("assets");
  const [query, setQuery] = useState("");
  const [showDisposed, setShowDisposed] = useState(false);
  const [editing, setEditing] = useState(null);   // asset object or "new"
  const [history, setHistory] = useState(null);
  const [canEdit, setCanEdit] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [a, e, li, pos] = await Promise.all([
        api.get(`/tenants/${tenantId}/wealth/assets`, { params: { include_disposed: showDisposed } }),
        api.get(`/tenants/${tenantId}/wealth/entities`),
        api.get(`/tenants/${tenantId}/wealth/liabilities`),
        api.get(`/tenants/${tenantId}/wealth/position`),
      ]);
      setAssets(a.data); setEntities(e.data); setLiabilities(li.data);
      setCanEdit(!!pos.data.can_edit);
    } catch (err) { toast.error(errMsg(err)); }
  }, [tenantId, showDisposed]);

  useEffect(() => { load(); }, [load]);

  const submitAsset = async (body) => {
    try {
      if (editing === "new") await api.post(`/tenants/${tenantId}/wealth/assets`, body);
      else await api.put(`/tenants/${tenantId}/wealth/assets/${editing.id}`, body);
      toast.success("Asset saved — a valuation record was written for provenance");
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const dispose = async (asset) => {
    if (!window.confirm(`Mark "${asset.name}" as disposed? It stays in history.`)) return;
    try {
      await api.post(`/tenants/${tenantId}/wealth/assets/${asset.id}/dispose`, {});
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openHistory = async (asset) => {
    try {
      const { data } = await api.get(`/tenants/${tenantId}/wealth/assets/${asset.id}/valuations`);
      setHistory({ asset, rows: data });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const rows = assets.filter((a) =>
    !query || `${a.name} ${a.asset_class} ${a.source_detail}`.toLowerCase().includes(query.toLowerCase()));
  const bandLabel = (key) =>
    (config?.liquidity_bands?.items || []).find((b) => b.key === key)?.label || key;
  const entityName = (id) => entities.find((e) => e.id === id)?.name || "—";

  return (
    <WealthShell
      title="Master asset & liability register"
      description="Each asset is entered once, valued by the appropriate method, and read everywhere else. Every save writes a dated valuation record with its source."
      actions={canEdit && tab === "assets" && !editing && (
        <button onClick={() => setEditing("new")} data-testid="register-add-asset"
                className="inline-flex items-center gap-2 rounded-full bg-plum px-5 py-3 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-plum/90">
          <Plus size={14} /> Add asset
        </button>
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        {[["assets", `Assets (${assets.length})`], ["liabilities", `Liabilities (${liabilities.length})`]].map(([k, label]) => (
          <button key={k} onClick={() => { setTab(k); setEditing(null); }} data-testid={`register-tab-${k}`}
                  className={`rounded-full px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] transition-colors ${
                    tab === k ? "bg-navy text-white" : "border border-border text-navy hover:border-navy/50"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "liabilities" ? (
        <LiabilityPanel liabilities={liabilities} assets={assets} entities={entities}
                        canEdit={canEdit} reload={load} />
      ) : editing ? (
        <AssetForm config={config} entities={entities}
                   initial={editing === "new" ? null : editing}
                   onCancel={() => setEditing(null)} onSubmit={submitAsset} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-1 items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5">
              <MagnifyingGlass size={15} className="text-muted-foreground" />
              <input value={query} onChange={(e) => setQuery(e.target.value)}
                     placeholder="Search assets, classes or sources" data-testid="register-search"
                     className="w-full bg-transparent text-sm outline-none" />
            </div>
            <label className="flex items-center gap-2 text-xs text-navy">
              <input type="checkbox" checked={showDisposed} data-testid="register-show-disposed"
                     onChange={(e) => setShowDisposed(e.target.checked)} />
              Include disposed
            </label>
          </div>

          <div className="panel overflow-x-auto" data-testid="asset-register-table">
            <table className="w-full min-w-[60rem] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  {["Asset", "Owner", "Bucket", "Class", "Liquidity", "Method", "Gross", "Liability", "Net", "Source", ""]
                    .map((h) => (
                      <th key={h} className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                        {h}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.length === 0 && (
                  <tr><td colSpan={11} className="px-4 py-6 text-center text-sm text-muted-foreground">
                    No assets recorded yet.
                  </td></tr>
                )}
                {rows.map((a) => (
                  <tr key={a.id} className={a.status === "disposed" ? "opacity-50" : ""}
                      data-testid={`asset-row-${a.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-navy">
                        {a.name}
                        {a.system_managed && <Lock size={12} className="ml-1.5 inline text-plum" />}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {a.valuation_method === "unit_price"
                          ? `${a.quantity?.toLocaleString("en-IN")} ${a.unit_type} × ${inr(a.price_per_unit)}`
                          : a.notes || ""}
                        {a.test_value && <span className="ml-1 font-semibold text-[hsl(28_82%_38%)]">· test value</span>}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-foreground/80">{entityName(a.owner_entity_id)}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] ${BUCKET_TONE[a.bucket]?.chip}`}>
                        {a.bucket}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground/80">{a.asset_class}</td>
                    <td className="px-4 py-3 text-foreground/80">{bandLabel(a.liquidity_band)}</td>
                    <td className="px-4 py-3 text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
                      {a.valuation_method.replace("_", " ")}
                    </td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-navy">{cr(a.gross_value)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-destructive">
                      {a.linked_liability ? cr(a.linked_liability) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold tabular-nums text-navy">{cr(a.net_value)}</td>
                    <td className="px-4 py-3">
                      <p className="text-[11px] text-foreground/80">{a.source_detail}</p>
                      <p className={`text-[11px] ${a.stale ? "font-semibold text-[hsl(28_82%_38%)]" : "text-muted-foreground"}`}>
                        {(a.valuation_date || "").slice(0, 10)}
                        {a.stale && <WarningCircle size={11} className="ml-1 inline" />}
                      </p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button onClick={() => openHistory(a)} title="Valuation history"
                              data-testid={`asset-history-${a.id}`}
                              className="rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-navy">
                        <ClockCounterClockwise size={14} />
                      </button>
                      {canEdit && a.status !== "disposed" && (
                        <>
                          <button onClick={() => setEditing(a)} data-testid={`asset-edit-${a.id}`}
                                  className="rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.12em] text-plum hover:underline">
                            Edit
                          </button>
                          <button onClick={() => dispose(a)} title="Mark disposed"
                                  data-testid={`asset-dispose-${a.id}`}
                                  className="rounded-full p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                            <Archive size={14} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {history && (
        <div className="panel p-6" data-testid="asset-history-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg text-navy">Valuation history — {history.asset.name}</h3>
            <button onClick={() => setHistory(null)} data-testid="asset-history-close"
                    className="rounded-full p-2 text-muted-foreground hover:bg-muted"><Trash size={14} /></button>
          </div>
          <div className="mt-4 divide-y divide-border">
            {history.rows.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-4 py-3 text-sm">
                <span className="font-mono tabular-nums text-navy">{cr(r.gross_value)}</span>
                <span className="text-foreground/70">
                  {r.quantity ? `${r.quantity.toLocaleString("en-IN")} × ${inr(r.price_per_unit)}` : "direct value"}
                </span>
                <span className="label-caps">{r.source_type?.replace(/_/g, " ")} · {r.source_detail}</span>
                <span className="ml-auto font-mono text-[11px] text-muted-foreground">
                  {(r.valuation_date || "").slice(0, 10)} · entered {(r.entered_at || "").slice(0, 10)} by {r.entered_by_email}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </WealthShell>
  );
}
